import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

/**
 * DocumentUploadLanding — the patient side of the secure document link.
 * Reached from the SMS at /docs/:token. No login: the patient proves who they
 * are with last name + date of birth, then either uploads what the practice
 * asked for or views what the practice sent. Nothing PHI is shown before the
 * check passes.
 */
function DocumentUploadLanding() {
  const { token } = useParams();
  const [state, setState] = useState(null);     // public state before verification
  const [error, setError] = useState(null);
  const [lastname, setLastname] = useState('');
  const [dob, setDob] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [session, setSession] = useState(null); // after verification
  const [uploaded, setUploaded] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.getDocLink(token)
      .then(r => { if (!cancelled) setState(r.data); })
      .catch(() => { if (!cancelled) setError('invalid'); });
    return () => { cancelled = true; };
  }, [token]);

  const verify = async (e) => {
    e.preventDefault();
    setVerifying(true); setVerifyError(null);
    try {
      const r = await api.verifyDocLink(token, lastname, dob);
      setSession(r.data);
      setUploaded(r.data.uploaded || []);
      setFiles(r.data.files || []);
    } catch (err) {
      setVerifyError(err.message || 'That did not match our records.');
      if (/expired/i.test(err.message)) setError('expired');
      if (/Too many/i.test(err.message)) setError('locked');
    } finally {
      setVerifying(false);
    }
  };

  // Review before sending: a chosen file is staged with a preview; nothing is
  // uploaded until the patient presses "Send to the practice".
  const [staged, setStaged] = useState({}); // category -> { file, preview }
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null); // "2 of 3"

  const stage = (category, file) => {
    if (!file) return;
    setUploadError(null);
    const preview = /^image\//.test(file.type) ? URL.createObjectURL(file) : null;
    setStaged(prev => {
      if (prev[category]?.preview) URL.revokeObjectURL(prev[category].preview);
      return { ...prev, [category]: { file, preview } };
    });
  };
  const unstage = (category) => setStaged(prev => {
    if (prev[category]?.preview) URL.revokeObjectURL(prev[category].preview);
    const n = { ...prev }; delete n[category]; return n;
  });

  const submitAll = async () => {
    const entries = Object.entries(staged);
    if (!entries.length) return;
    setSending(true); setUploadError(null);
    let sent = 0;
    try {
      for (const [category, { file }] of entries) {
        setProgress(`${sent + 1} of ${entries.length}`);
        const r = await api.uploadDocLinkFile(token, session.session, category, file);
        setUploaded(r.data.uploaded || []);
        unstage(category);
        sent += 1;
      }
    } catch (err) {
      setUploadError(err.message || 'Upload failed. Please try again.');
      if (/session has expired/i.test(err.message)) setSession(null);
    } finally {
      setSending(false); setProgress(null);
    }
  };

  const practice = state?.practice_name || 'Your care team';

  // The patient arrived here from a text message, so "reply to that text" is the
  // one instruction that always works — this page has no phone number of its own.
  if (error === 'invalid' || (state && !state.valid && state.reason === 'not_found')) {
    return (
      <Shell title="This link isn't working">
        <p style={s.sub}>The link may have been copied incompletely, or it has already been replaced by a newer one.</p>
        <p style={s.sub}>Reply to the text message you received and {practice} will send you a fresh link.</p>
      </Shell>
    );
  }
  if (error === 'expired' || (state && !state.valid && state.reason === 'expired')) {
    return (
      <Shell title="This link has expired">
        <p style={s.sub}>For your privacy these links stay open for 7 days, and this one has now closed. Nothing you sent earlier has been lost.</p>
        <p style={s.sub}>Reply to the text message you received and {practice} will send you a new link.</p>
      </Shell>
    );
  }
  if (error === 'locked' || (state && !state.valid && state.reason === 'locked')) {
    return (
      <Shell title="This link is locked">
        <p style={s.sub}>The date of birth or last name didn't match after several tries, so we closed the link to keep your information safe.</p>
        <p style={s.sub}>Reply to the text message you received and {practice} will send you a new link.</p>
      </Shell>
    );
  }
  if (!state) {
    return <Shell title="One moment…"><p style={s.sub}>Loading your secure link.</p></Shell>;
  }

  if (!session) {
    return (
      <Shell title={practice} eyebrow="Secure document link">
        <p style={s.sub}>
          {state.direction === 'inbound'
            ? 'Before you upload, please confirm it’s you.'
            : 'Before we show your documents, please confirm it’s you.'}
        </p>
        <form onSubmit={verify} style={s.form}>
          <label style={s.label}>Last name
            <input style={s.input} value={lastname} onChange={e => setLastname(e.target.value)} autoComplete="family-name" required />
          </label>
          <label style={s.label}>Date of birth
            <input style={s.input} type="date" value={dob} onChange={e => setDob(e.target.value)} required />
          </label>
          {verifyError && <div style={s.err}>{verifyError}</div>}
          <button type="submit" style={s.primary} disabled={verifying || !lastname || !dob}>{verifying ? 'Checking…' : 'Continue'}</button>
        </form>
        <p style={s.fine}>Your details are checked against the practice’s records and are not stored on this page.</p>
      </Shell>
    );
  }

  if (session.direction === 'outbound') {
    return (
      <Shell title={`Documents from ${practice}`} eyebrow={`Hi ${session.patient_first_name || ''}`.trim()}>
        {session.note && <p style={s.sub}>{session.note}</p>}
        <div style={s.list}>
          {files.map(f => (
            <a key={f.id} href={f.url} target="_blank" rel="noopener noreferrer" style={s.fileRow}>
              <span style={s.fileIcon}>{/pdf/i.test(f.mime_type || '') ? 'PDF' : 'IMG'}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={s.fileName}>{f.name}</span>
                <span style={s.fileMeta}>{formatBytes(f.size_bytes)} · opens in a new tab</span>
              </span>
              <span style={s.chev}>›</span>
            </a>
          ))}
          {files.length === 0 && <p style={s.sub}>Nothing to show yet.</p>}
        </div>
        <p style={s.fine}>Each link opens for 15 minutes. Reopen this page if a document stops loading.</p>
      </Shell>
    );
  }

  const done = new Set(uploaded.map(u => u.category));
  const allDone = session.categories.every(c => done.has(c.key));
  const stagedCount = Object.keys(staged).length;
  return (
    <Shell title={allDone && !stagedCount ? 'All received, thank you' : `${practice} needs a few documents`} eyebrow={`Hi ${session.patient_first_name || ''}`.trim()}>
      {session.note && <p style={s.sub}>{session.note}</p>}
      {!allDone && !stagedCount && <p style={s.sub}>Take a photo or choose a file for each item, check the preview, then send them all at once.</p>}
      <div style={s.list}>
        {session.categories.map(c => (
          <UploadRow key={c.key} category={c} done={done.has(c.key)} staged={staged[c.key] || null} disabled={sending}
            uploadedName={uploaded.find(u => u.category === c.key)?.file_name}
            onPick={file => stage(c.key, file)} onRemove={() => unstage(c.key)} />
        ))}
      </div>
      {uploadError && <div style={s.err}>{uploadError}</div>}
      {stagedCount > 0 && (
        <button type="button" style={s.primary} disabled={sending} onClick={submitAll}>
          {sending ? `Sending ${progress || ''}…` : `Send ${stagedCount === 1 ? 'this file' : `these ${stagedCount} files`} to the practice`}
        </button>
      )}
      <p style={s.fine}>Nothing is sent until you press Send. Files go over an encrypted connection and are stored securely for the practice. Up to 10 MB each; JPG, PNG, HEIC or PDF.</p>
    </Shell>
  );
}

function UploadRow({ category, done, staged, disabled, uploadedName, onPick, onRemove }) {
  const inputRef = useRef(null);
  const state = staged ? 'staged' : done ? 'done' : 'empty';
  const border = state === 'staged' ? '#C4B5FD' : state === 'done' ? '#A7F3D0' : '#E6E2F3';
  const bg = state === 'staged' ? '#F7F3FF' : state === 'done' ? '#F0FDF4' : '#fff';
  return (
    <div style={{ ...s.fileRow, cursor: 'default', borderColor: border, background: bg, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {staged?.preview
        ? <img src={staged.preview} alt="" style={s.thumb} />
        : <span style={{ ...s.fileIcon, background: state === 'done' ? '#16A34A' : state === 'staged' ? '#7C3AED' : '#5B3DF5' }}>{state === 'done' ? '✓' : staged ? 'PDF' : (category.image ? '📷' : '📄')}</span>}
      <span style={{ flex: 1, minWidth: 140 }}>
        <span style={s.fileName}>{category.label}</span>
        <span style={s.fileMeta}>
          {state === 'staged' ? `Ready to send · ${staged.file.name} · ${formatBytes(staged.file.size)}` : state === 'done' ? `Received · ${uploadedName || 'file'}` : 'Not yet added'}
        </span>
      </span>
      {/* No `capture` attribute: with it, phones open the camera only and never offer the photo library or files. */}
      <input ref={inputRef} type="file" accept="image/*,application/pdf"
        style={{ display: 'none' }} onChange={e => { onPick(e.target.files?.[0]); e.target.value = ''; }} />
      <span style={{ display: 'flex', gap: 6 }}>
        {state === 'staged' && <button type="button" style={s.secondary} disabled={disabled} onClick={onRemove}>Remove</button>}
        <button type="button" style={state === 'empty' ? s.primarySmall : s.secondary} disabled={disabled} onClick={() => inputRef.current?.click()}>
          {state === 'staged' ? 'Choose another' : state === 'done' ? 'Replace' : (category.image ? 'Take photo' : 'Choose file')}
        </button>
      </span>
    </div>
  );
}

function Shell({ title, eyebrow, children }) {
  return (
    <div style={s.page}>
      <div style={s.card}>
        {eyebrow && <div style={s.eyebrow}>{eyebrow}</div>}
        <h1 style={s.title}>{title}</h1>
        {children}
        <div style={s.footer}>Powered by NH Connect · Secure patient messaging</div>
      </div>
    </div>
  );
}

function formatBytes(n) {
  if (!n) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(160deg, #F6F4FB 0%, #EFEAFF 100%)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px' },
  card: { width: '100%', maxWidth: 520, background: '#fff', borderRadius: 18, padding: '26px 22px', boxShadow: '0 16px 40px rgba(29,26,51,.12)', color: '#1D1A33', fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  eyebrow: { fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: '#5B3DF5', marginBottom: 6 },
  title: { margin: '0 0 10px', fontSize: 22, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.25 },
  sub: { margin: '0 0 14px', color: '#534D6E', fontSize: 15, lineHeight: 1.5 },
  form: { display: 'grid', gap: 12, marginTop: 6 },
  label: { display: 'grid', gap: 6, fontSize: 13, fontWeight: 700, color: '#534D6E' },
  input: { font: 'inherit', fontSize: 16, padding: '12px 14px', border: '1.5px solid #E6E2F3', borderRadius: 12, outline: 'none', color: '#1D1A33', background: '#FBFAFF' },
  primary: { font: 'inherit', fontSize: 16, fontWeight: 800, color: '#fff', background: 'linear-gradient(135deg, #5B3DF5, #8B5CF6)', border: 0, borderRadius: 12, padding: '13px 18px', cursor: 'pointer', boxShadow: '0 6px 16px rgba(91,61,245,.3)' },
  primarySmall: { font: 'inherit', fontSize: 14, fontWeight: 800, color: '#fff', background: 'linear-gradient(135deg, #5B3DF5, #8B5CF6)', border: 0, borderRadius: 10, padding: '10px 14px', cursor: 'pointer', whiteSpace: 'nowrap' },
  secondary: { font: 'inherit', fontSize: 13, fontWeight: 700, color: '#166534', background: '#DCFCE7', border: 0, borderRadius: 10, padding: '9px 12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  err: { background: '#FFE4E9', color: '#9F1239', fontSize: 13.5, fontWeight: 600, padding: '10px 12px', borderRadius: 10, margin: '10px 0' },
  fine: { fontSize: 12, color: '#8B86A3', margin: '14px 0 0', lineHeight: 1.5 },
  list: { display: 'grid', gap: 10, margin: '12px 0' },
  fileRow: { display: 'flex', alignItems: 'center', gap: 12, border: '1.5px solid #E6E2F3', borderRadius: 14, padding: '12px 14px', textDecoration: 'none', color: 'inherit', background: '#fff' },
  thumb: { width: 64, height: 48, borderRadius: 10, objectFit: 'cover', flexShrink: 0, border: '1px solid #C4B5FD' },
  fileIcon: { width: 40, height: 40, borderRadius: 12, background: '#5B3DF5', color: '#fff', fontWeight: 800, fontSize: 12, display: 'grid', placeItems: 'center', flexShrink: 0 },
  fileName: { display: 'block', fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileMeta: { display: 'block', fontSize: 12.5, color: '#8B86A3', marginTop: 2 },
  chev: { color: '#8B86A3', fontSize: 22 },
  footer: { marginTop: 22, paddingTop: 12, borderTop: '1px solid #E6E2F3', fontSize: 11, color: '#8B86A3', textAlign: 'center' },
};

export default DocumentUploadLanding;
